import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../api";

const QRPublicForm = ({ showToast }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [delayDone, setDelayDone] = useState(false);
  const [form, setForm] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSentAt, setEmailSentAt] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  useEffect(() => {
    let mounted = true;
    //ensure we don't show not found immediately
    const t = setTimeout(() => setDelayDone(true), 2000);
    const fetchForm = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/qr/${id}`);
        if (!mounted) return;
        setForm(data);
        const initial = {};
        (data.questions || []).forEach((q, idx) => {
          const key = q.id || q.key || `q${idx + 1}`;
          initial[key] = "";
        });
        setAnswers(initial);
      } catch (err) {
        console.error(err);
        showToast && showToast("Form not found", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchForm();
    return () => { mounted = false; clearTimeout(t); };
  }, [id, showToast]);

  // Capture user location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          console.log('Location captured:', position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.warn('Location error:', error);
          setLocationError(error.message);
          // Don't show toast - location is optional
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setLocationError('Geolocation not supported');
    }
  }, []);

  const handleChange = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  // Check if a question should be shown based on its showIf condition
  const shouldShowQuestion = (question) => {
    if (!question.showIf) return true; // No condition means always show
    
    const { questionId, value } = question.showIf;
    const answerValue = answers[questionId];
    
    // Handle array values (checkbox)
    if (Array.isArray(answerValue)) {
      return answerValue.includes(value);
    }
    
    // Handle string comparison
    return answerValue === value;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { answers };
      
      // Include location if available
      if (userLocation) {
        payload.location = {
          latitude: userLocation.latitude,
          longitude: userLocation.longitude
        };
      }
      
      const { data } = await api.post(`/qr/${id}/submit`, payload);
      setEmailSentAt(data?.emailSentAt || null);
      showToast && showToast("Response submitted", "success");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      showToast && showToast("Failed to submit", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForAnother = () => {
    const initial = {};
    (form.questions || []).forEach((q, idx) => {
      const key = q.id || q.key || `q${idx + 1}`;
      initial[key] = "";
    });
    setAnswers(initial);
    setSubmitted(false);
    setEmailSentAt(null);
  };

  const outerStyle = {
    maxWidth: "100%",
    margin: 0,
    padding: 24,
    border: "none",
    borderRadius: 0,
    background: "var(--bg)",
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    color: "var(--text)",
  };

  if (loading || (!form && !delayDone)) {
    return (
      <div className="page-content" style={outerStyle}>Loading...</div>
    );
  }
  if (!form && delayDone) {
    return (
      <div className="page-content" style={outerStyle}>Form not found</div>
    );
  }

  return (
    <div className="page-content dark-theme" style={outerStyle}>
      <div style={{ width: 640, marginTop: 24, background: form.bgColor || "var(--hover-bg)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8, boxShadow: `0 6px 24px var(--shadow)`, padding: 20 }}>
        {form.logoUrl && (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img 
              src={form.logoUrl} 
              alt={form.orgName || "Organization logo"} 
              style={{ 
                maxWidth: '100%', 
                maxHeight: 120, 
                objectFit: 'contain',
                borderRadius: 4
              }} 
            />
          </div>
        )}
        <h3 className="bold" style={{ marginBottom: 8 }}>{form.orgName || "QR Form"}</h3>
        <p style={{ marginBottom: 16, opacity: 0.85 }}>{submitted ? "Thank you for your response" : "Please answer the questions below"}</p>
      {!submitted ? (
        <form onSubmit={handleSubmit}>
          {(form.questions || []).map((q, idx) => {
            const key = q.id || q.key || `q${idx + 1}`;
            const label = q.label || q.question || `Question ${idx + 1}`;
            const type = q.type || "text";
            const required = !!q.required;
            const value = answers[key] || (type === "checkbox" ? [] : "");
            
            // Check if question should be displayed based on conditions
            if (!shouldShowQuestion(q)) {
              return null;
            }
            
            if (type === 'header') {
              return (
                <div key={key} style={{ margin: '12px 0 4px' }}>
                  <h4 style={{ margin: 0 }}>{q.title || label}</h4>
                </div>
              );
            }
            if (type === 'section') {
              return (
                <div key={key} style={{ margin: '12px 0' }}>
                  {q.title && <div style={{ fontWeight: 600, marginBottom: 4 }}>{q.title}</div>}
                  {q.showDivider && <div style={{ height: 1, background: 'var(--border)', margin: '6px 0' }} />}
                </div>
              );
            }
            if (type === 'static_text') {
              return (
                <div key={key} style={{ margin: '8px 0', opacity: 0.9 }}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{q.content || ''}</div>
                </div>
              );
            }
            if (type === 'divider') {
              return (
                <div key={key} style={{ height: 1, background: 'var(--border)', margin: '8px 0' }} />
              );
            }
            return (
              <div className="form-group" key={key}>
                <label>{label}</label>
                {type === "text" && (
                  <input value={value} onChange={(e) => handleChange(key, e.target.value)} required={required} />
                )}
                {type === "date" && (
                  <input type="date" value={value} onChange={(e) => handleChange(key, e.target.value)} required={required} />
                )}
                {type === "multiple" && (
                  <select value={value} onChange={(e) => handleChange(key, e.target.value)} required={required}>
                    <option value="" disabled>{required ? "Select an option *" : "Select an option"}</option>
                    {(q.options || []).map((opt, oIdx) => (
                      <option key={oIdx} value={opt}>{opt}</option>
                    ))}
                  </select>
                )}
                {type === "checkbox" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", rowGap: 6, columnGap: 8 }}>
                    {(q.options || []).map((opt, oIdx) => {
                      const checked = Array.isArray(value) && value.includes(opt);
                      return (
                        <React.Fragment key={oIdx}>
                          <div style={{ justifySelf: "start" }}>{opt}</div>
                          <input
                            type="checkbox"
                            style={{ justifySelf: "end" }}
                            checked={checked}
                            onChange={(e) => {
                              let next = Array.isArray(value) ? value.slice() : [];
                              if (e.target.checked) {
                                if (!next.includes(opt)) next.push(opt);
                              } else {
                                next = next.filter(v => v !== opt);
                              }
                              handleChange(key, next);
                            }}
                          />
                        </React.Fragment>
                      );
                    })}
                    {required && (!Array.isArray(value) || value.length === 0) && (
                      <span style={{ fontSize: 12, color: "#d9534f" }}>Please select at least one option.</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Location status indicator */}
          <div style={{ 
            marginTop: 12, 
            padding: 8, 
            borderRadius: 4, 
            fontSize: 12, 
            background: userLocation ? 'rgba(40, 167, 69, 0.1)' : 'rgba(255, 193, 7, 0.1)',
            color: userLocation ? '#28a745' : '#856404',
            border: `1px solid ${userLocation ? 'rgba(40, 167, 69, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`,
            display: 'flex',
            alignItems: 'center',
            gap: 6
          }}>
            {userLocation ? (
              <>
                <span>📍</span>
                <span>Location captured (accuracy: {Math.round(userLocation.accuracy)}m)</span>
              </>
            ) : (
              <>
                <span>📍</span>
                <span>{locationError ? `Location unavailable: ${locationError}` : 'Attempting to capture location...'}</span>
              </>
            )}
          </div>
          
          <div className="form-actions" style={{ marginTop: 16 }}>
            <button type="submit" className="btn btn-success" disabled={submitting}>{submitting ? "Submitting..." : "Submit"}</button>
          </div>
        </form>
      ) : (
        <div>
          <div className="message" style={{ marginBottom: 12 }}>Your response has been submitted.</div>
          {emailSentAt && (
            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 12, textAlign: "center" }}>A notification email was sent to the organizer.</div>
          )}
          <div className="form-actions" style={{ display: "flex", justifyContent: "center" }}>
            <button type="button" className="btn btn-primary" onClick={resetForAnother}>Submit another response</button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default QRPublicForm;


